import type { NotificationApiAdapter } from '../types';

const NOTIFICATION_STREAM_TOKEN_ENDPOINT = '/external/api/v1/jobseeker/token';
const NOTIFICATION_STREAM_ENDPOINT = '/external/jobseeker/stream';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export async function getNotificationStreamToken(
  api: NotificationApiAdapter,
  signal: AbortSignal
): Promise<string> {
  const response = await api.get<unknown>(
    NOTIFICATION_STREAM_TOKEN_ENDPOINT,
    undefined,
    { signal, timeoutMs: 15_000 }
  );

  if (isRecord(response) && response.status && response.status !== 'success') {
    throw new Error('Không thể lấy token thông báo.');
  }

  const data = isRecord(response) ? response.data : undefined;
  const token = isRecord(data)
    ? data.token
    : typeof data === 'string'
      ? data
      : isRecord(response)
        ? response.token
        : response;

  if (typeof token !== 'string' || !token.trim() || token.length > 4096) {
    throw new Error('Token thông báo chưa đúng định dạng.');
  }

  return token.trim();
}

export function getNotificationStreamUrl(
  api: NotificationApiAdapter,
  userId: string,
  token: string
) {
  const url = new URL(
    `${api.baseUrl.replace(/\/$/, '')}${NOTIFICATION_STREAM_ENDPOINT}`
  );

  url.searchParams.set('userId', userId);
  url.searchParams.set('token', token);

  return url.toString();
}

export function isNotificationStreamUpdate(event: MessageEvent<string>) {
  const value = event.data.trim();

  // Transport control messages must never cause REST traffic or UI updates.
  if (!value || /^(ping|pong|heartbeat|connected)$/i.test(value)) return false;
  if (value.length > 65_536) return true;

  try {
    const payload: unknown = JSON.parse(value);

    if (isRecord(payload)) {
      const type = payload.type ?? payload.event;

      if (
        typeof type === 'string' &&
        /^(ping|pong|heartbeat|connected)$/i.test(type)
      ) {
        return false;
      }
    }
  } catch {
    // A notification invalidation may be plain text; its body is not stored.
  }

  return true;
}
